import React from 'react';
import { Alert, Empty, Skeleton, Space, Tabs, Tag, Typography } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { SubsystemId } from '../../services/ai-admin-api';
import { AI_HUB_SUBSYSTEMS } from './ai-hub-routes';
import { VersionsTab } from './tabs/VersionsTab';
import { ActivityTab } from './tabs/ActivityTab';
import { SandboxTab } from './tabs/SandboxTab';
import {
    SubsystemEditorProvider,
    useSubsystemDraftEditor,
} from './hooks/useSubsystemDraftEditor';
import { SharedPromptLibraryConfigurePage } from './subsystems/SharedPromptLibraryConfigurePage';
import { ModelsProvidersConfigurePage } from './subsystems/ModelsProvidersConfigurePage';
import { AiExpertsConfigurePage } from './subsystems/AiExpertsConfigurePage';
import { ElementAnalysisConfigurePage } from './subsystems/ElementAnalysisConfigurePage';
import { AdviceAiConfigurePage } from './subsystems/AdviceAiConfigurePage';
import { OrbitalAiConfigurePage } from './subsystems/OrbitalAiConfigurePage';
import { BuildingBlocksAiConfigurePage } from './subsystems/BuildingBlocksAiConfigurePage';
import { TheBriefingAiConfigurePage } from './subsystems/TheBriefingAiConfigurePage';
import { TheHypothesisAiConfigurePage } from './subsystems/TheHypothesisAiConfigurePage';
import { ModelBeliefsAiConfigurePage } from './subsystems/ModelBeliefsAiConfigurePage';
import { AutoConciergeAiConfigurePage } from './subsystems/AutoConciergeAiConfigurePage';
import { TokenQuotasConfigurePage } from './subsystems/TokenQuotasConfigurePage';
import { KnowledgeBaseConfigurePage } from './subsystems/KnowledgeBaseConfigurePage';
import { ResponseMonitoringAiConfigurePage } from './subsystems/ResponseMonitoringAiConfigurePage';
import { EditorAiConfigurePage } from './subsystems/EditorAiConfigurePage';
import { ModelBeliefsRunsPanel } from '../components/model-beliefs/ModelBeliefsRunsPanel';
import { ModelBeliefsBrandsPanel } from '../components/model-beliefs/ModelBeliefsBrandsPanel';
import { ModelBeliefsStatementsPanel } from '../components/model-beliefs/ModelBeliefsStatementsPanel';

const CONFIGURE_BODIES: Partial<Record<string, React.ReactNode>> = {
    shared_prompt_library: <SharedPromptLibraryConfigurePage />,
    models_providers: <ModelsProvidersConfigurePage />,
    ai_experts: <AiExpertsConfigurePage />,
    element_analysis: <ElementAnalysisConfigurePage />,
    advice_ai: <AdviceAiConfigurePage />,
    orbital_ai: <OrbitalAiConfigurePage />,
    building_blocks_ai: <BuildingBlocksAiConfigurePage />,
    briefing_ai: <TheBriefingAiConfigurePage />,
    hypothesis_ai: <TheHypothesisAiConfigurePage />,
    model_beliefs_ai: <ModelBeliefsAiConfigurePage />,
    auto_concierge_ai: <AutoConciergeAiConfigurePage />,
    token_quotas: <TokenQuotasConfigurePage />,
    knowledge_base: <KnowledgeBaseConfigurePage />,
    response_monitoring_ai: <ResponseMonitoringAiConfigurePage />,
    editor_ai: <EditorAiConfigurePage />,
};

/**
 * Subsystems that don't run an LLM call as part of their normal operation —
 * they configure other subsystems' behaviour. Sandbox tab shows an explanatory
 * alert instead of the runner UI.
 */
/**
 * Subsystems that also have an operational surface — what ran, what it cost,
 * what it found. Configure edits prompts and settings everywhere in the Hub,
 * and the draft/version machinery hangs off that contract, so a runs table
 * gets its own tab rather than sitting inside Configure meaning something
 * different from the other fourteen.
 */
const OPERATIONS_TABS: Partial<Record<string, { key: string; label: string; body: React.ReactNode }[]>> = {
    model_beliefs_ai: [
        { key: 'runs', label: 'Runs', body: <ModelBeliefsRunsPanel /> },
        // Per-brand config, not prompts: what a shopper calls the category,
        // which market, whose domains. A read cannot happen without it.
        { key: 'brands', label: 'Brands', body: <ModelBeliefsBrandsPanel /> },
        // The instrument itself: what is asked and what each statement means to
        // the read. Not Configure, which is prompts and settings.
        { key: 'statements', label: 'Statements', body: <ModelBeliefsStatementsPanel /> },
    ],
};

const SANDBOX_NOT_APPLICABLE: Partial<Record<string, string>> = {
    shared_prompt_library:
        'Shared placeholders are referenced by other subsystems — sandbox a downstream subsystem (e.g. Advice AI) to see the effect of changes here.',
    models_providers:
        'Use the per-row "Test connection" button on the Configure tab for connectivity checks. Sandbox a downstream subsystem (e.g. Advice AI) to compare provider/model swaps end-to-end.',
    token_quotas:
        'Token Quotas controls accounting and rate-limit policy — there is no prompt to run. Effects show up in any downstream subsystem\'s sandbox runs.',
    knowledge_base:
        'Knowledge Base is a retrieval source — sandbox AI Experts to compare RAG behaviour with different chunking/retrieval settings.',
    editor_ai:
        'Editor AI edits the corporate marketing site — there are no brand-scoped inputs to compare. Iterate on the system prompt directly via Save Draft / Publish, then test by issuing a request in the editor itself with the new prompt active.',
    response_monitoring_ai:
        'Response Monitoring AI checks weekly response-collection volumes per brand and flags drops — there is no brand-scoped prompt to A/B compare. Tune the schedule + thresholds + notification copy directly via Save Draft / Publish; the next scheduled run will use the new config.',
    auto_concierge_ai:
        'The pack is written from live brand research and is addressed to a real contact, so there is no safe A/B runner. To test prompt changes, publish them and use "Build a pack" on an organisation\'s Concierge tab — the draft lands in the approval queue and sends nothing until approved.',
    hypothesis_ai:
        'The Hypothesis runs against live web research (site crawl + review fetch), so an A/B sandbox is not wired up yet. To test prompt changes, publish them and use "Trigger for brand" on the Configure tab against a test brand — the research job re-runs end-to-end.',
};

const { Title, Text } = Typography;

/**
 * Reusable per-subsystem shell. Header (name + subtitle + version + draft
 * badge), tab nav (Configure / Sandbox / Versions / Activity), tab body.
 *
 * Owns the `useSubsystemDraftEditor` instance and provides it via context so
 * the header badges and the Configure body see one source of truth — without
 * this the header would go stale right after a publish bumped the version.
 */
export const SubsystemPageShell: React.FC = () => {
    const { subsystemId } = useParams<{ subsystemId: string }>();
    const navigate = useNavigate();
    const navEntry = AI_HUB_SUBSYSTEMS.find((s) => s.id === subsystemId);

    if (!navEntry) {
        return (
            <Empty description={`Unknown subsystem: ${subsystemId}`}>
                <a onClick={() => navigate('/admin/ai')}>Back to hub</a>
            </Empty>
        );
    }

    return <SubsystemPageBody key={navEntry.id} navEntry={navEntry} />;
};

interface BodyProps {
    navEntry: typeof AI_HUB_SUBSYSTEMS[number];
}

/**
 * Inner body so we can call the editor hook keyed by the resolved subsystem
 * id and re-mount cleanly when the URL changes between subsystems.
 */
const SubsystemPageBody: React.FC<BodyProps> = ({ navEntry }) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const editor = useSubsystemDraftEditor(navEntry.id);
    const detail = editor.detail;

    return (
        <SubsystemEditorProvider value={editor}>
            <div>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space size="small" wrap>
                        <Title level={2} style={{ margin: 0 }}>
                            {navEntry.displayName}
                        </Title>
                        {detail?.currentPublishedVersion ? (
                            <Tag color="blue">v{detail.currentPublishedVersion}</Tag>
                        ) : (
                            <Tag>v?</Tag>
                        )}
                        {detail?.hasDraft && <Tag color="orange">Draft pending</Tag>}
                    </Space>
                    <Text type="secondary">{navEntry.subtitle}</Text>
                </Space>

                <Tabs
                    style={{ marginTop: 16 }}
                    items={[
                        {
                            key: 'configure',
                            label: 'Configure',
                            children: editor.isLoading && !detail ? (
                                <Skeleton active />
                            ) : (
                                <>
                                    {(editor.isDirty || detail?.hasDraft) && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            style={{ marginBottom: 12 }}
                                            message={
                                                editor.isDirty
                                                    ? 'You have unsaved edits in this draft.'
                                                    : 'A draft is pending publish.'
                                            }
                                            description="Changes don't reach the customer dashboard until you click Save Draft (if dirty) and then Publish."
                                        />
                                    )}
                                    {CONFIGURE_BODIES[navEntry.id] ?? (
                                        <Alert
                                            type="error"
                                            showIcon
                                            message={`Missing Configure body for "${navEntry.displayName}"`}
                                            description="Every Phase 3 subsystem should be wired into CONFIGURE_BODIES. Add an entry there to fix this."
                                        />
                                    )}
                                </>
                            ),
                        },
                        ...(OPERATIONS_TABS[navEntry.id] ?? []).map((tab) => ({
                            key: tab.key,
                            label: tab.label,
                            children: tab.body,
                        })),
                        {
                            key: 'sandbox',
                            label: 'Sandbox',
                            children: (
                                <SandboxTab
                                    subsystem={navEntry.id as SubsystemId}
                                    jwt={jwt}
                                    notApplicable={
                                        SANDBOX_NOT_APPLICABLE[navEntry.id]
                                            ? { reason: SANDBOX_NOT_APPLICABLE[navEntry.id]! }
                                            : undefined
                                    }
                                />
                            ),
                        },
                        {
                            key: 'versions',
                            label: 'Versions',
                            children: (
                                <VersionsTab
                                    subsystem={navEntry.id as SubsystemId}
                                    jwt={jwt}
                                />
                            ),
                        },
                        {
                            key: 'activity',
                            label: 'Activity',
                            children: (
                                <ActivityTab subsystem={navEntry.id as SubsystemId} />
                            ),
                        },
                    ]}
                />
            </div>
        </SubsystemEditorProvider>
    );
};
